import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ArrowLeft, MapPin, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GroupIcon } from "@/pages/Community";

import RichContent from "@/components/RichContent";
const Avatar = ({ name, avatar }) =>
  avatar ? (
    <img src={avatar} alt={name} className="h-24 w-24 rounded-full object-cover" />
  ) : (
    <span className="grid place-items-center h-24 w-24 rounded-full bg-[#144F75]/10 text-[#144F75] text-4xl font-semibold">
      {(name || "?").slice(0, 1).toUpperCase()}
    </span>
  );

export default function MemberProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: m, isLoading, error } = useQuery({
    queryKey: ["member", id],
    queryFn: () => api.get(`/members/${id}`).then((r) => r.data),
    retry: false,
  });

  if (isLoading) return <div className="grid place-items-center py-40 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>;
  if (error || !m) return <div className="text-center py-40 text-[#4A5D6B]">Member not found. <Link to="/community/members" className="text-[#144F75]">Directory</Link></div>;

  return (
    <div data-testid="member-profile-page" className="max-w-[800px] mx-auto px-6 lg:px-10 py-12">
      <Link to="/community/members" className="inline-flex items-center gap-1 text-sm text-[#4A5D6B] hover:text-[#144F75] mb-6"><ArrowLeft size={15} /> Directory</Link>
      <div className="eyw-card p-8">
        <div className="flex items-center gap-5">
          <Avatar name={m.name} avatar={m.avatar} />
          <div>
            <h1 className="font-serif text-3xl text-[#1A242B]">{m.name}</h1>
            <p className="text-[#144F75] capitalize">{m.role}</p>
            {m.headline && <p className="text-[#4A5D6B] mt-1">{m.headline}</p>}
            {m.location && <p className="text-sm text-[#8FA89B] flex items-center gap-1 mt-1"><MapPin size={12} /> {m.location}</p>}
          </div>
          {user && user.id !== id && (
            <Link to={`/messages?to=${id}`} data-testid="member-message-btn" className="eyw-btn !py-2 !px-4 text-sm ml-auto self-start">
              <MessageSquare size={15} /> Message
            </Link>
          )}
        </div>
        {m.bio && <RichContent text={m.bio} className="text-[#4A5D6B] leading-relaxed mt-6" />}
        {m.groups?.length > 0 && (
          <div className="mt-8">
            <p className="eyw-label mb-3">Groups</p>
            <div className="flex flex-wrap gap-2">
              {m.groups.map((g) => (
                <Link key={g.id} to={`/community/groups/${g.slug}`} className="inline-flex items-center gap-2 rounded-full border border-[#E5E0D8] px-3 py-1.5 text-sm text-[#1A242B] hover:border-[#144F75]">
                  <GroupIcon name={g.icon} size={14} className="text-[#D98E73]" /> {g.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
